import {
  EGameConfigs,
  IMapConfigEntry,
  IMapConfigEntryDomination,
  IMapConfigEntryOrLoose,
  IMapEntityBotLoose,
  IMapEntityLoose,
  IRoomLoose,
  ISpawnConfigLoose,
  ISpawnSetLoose,
  MatchType,
  MatchTypeWire
} from '@nova/types';
import { ReadonlyDeep, WritableDeep } from 'type-fest';

import { mapConfigDominationLiteral, mapConfigOrLiteral, mapConfigTfLiteral } from '../configs';

import { EntityDatasetKey } from './entity-types';

// ==============================
//  Strict Map Entity Types
// ==============================

/**
 * Map entity with type-safe entity dataset key.
 */
export interface IMapEntity extends Omit<IMapEntityLoose, 'id'> {
  id: EntityDatasetKey;
}

/**
 * Map entity bot with type-safe entity dataset key.
 */
export interface IMapEntityBot extends Omit<IMapEntityBotLoose, 'id'> {
  id: EntityDatasetKey;
}

export interface ISpawnSet extends Omit<ISpawnSetLoose, 'entities'> {
  entities: IMapEntity[];
}

export interface ISpawnConfig extends Omit<ISpawnConfigLoose, 'spawnSets'> {
  spawnSets: ISpawnSet[];
}

export interface IRoom extends Omit<IRoomLoose, 'initEntities' | 'initBots'> {
  initEntities: IMapEntity[];
  initBots?: IMapEntityBot[];
}

export type RoomSet = { [roomIndex: string]: IRoom | undefined };

export interface IMapConfigEntryOr extends Omit<IMapConfigEntryOrLoose, 'rooms'> {
  rooms: RoomSet;
}

export function isMapConfigEntryOr(mapConfig: IMapConfigEntry): mapConfig is IMapConfigEntryOr {
  // MatchType values are defined equal to MatchTypeWire literals (outpostRuns === 'trenchRun'); cast is TS-only.
  return mapConfig.type === (MatchType.outpostRuns as MatchTypeWire)
    || 'rooms' in mapConfig;
}

// ==============================
//  Map Config Literals
// ==============================

mapConfigOrLiteral satisfies ReadonlyDeep<IMapConfigEntry[]>;
type MapConfigOrLiteral = typeof mapConfigOrLiteral;
export type MapConfigOr = WritableDeep<MapConfigOrLiteral>;

mapConfigDominationLiteral satisfies ReadonlyDeep<IMapConfigEntryDomination[]>;
type MapConfigDominationLiteral = typeof mapConfigDominationLiteral;
export type MapConfigDomination = WritableDeep<MapConfigDominationLiteral>;

mapConfigTfLiteral satisfies ReadonlyDeep<IMapConfigEntry[]>;
type MapConfigTfLiteral = typeof mapConfigTfLiteral;
export type MapConfigTf = WritableDeep<MapConfigTfLiteral>;

export type MapConfigTypeToConfigKeyMap = {
  [EGameConfigs.MAPS_OR]: MapConfigOr;
  [EGameConfigs.MAPS_DOMINATION]: MapConfigDomination;
  [EGameConfigs.MAPS_TF]: MapConfigTf;
};
